import React from 'react';
import { DollarSign, ShieldCheck, Plane, Award, Users, FileCheck, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { aboutData } from '../data/mock';

const iconMap = {
  DollarSign,
  ShieldCheck,
  Plane,
  Award,
  Users,
  FileCheck
};

const About = () => {
  const handleWhatsAppClick = (number) => {
    window.open(`https://wa.me/${number.replace(/[^0-9]/g, '')}`, '_blank');
  };

  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center space-y-4 mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
            {aboutData.title}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            {aboutData.description}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {aboutData.stats.map((stat, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <p className="text-5xl font-bold text-gray-800 mb-2">{stat.number}</p>
              <p className="text-gray-700 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Advantages Section */}
        <div className="space-y-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Why Choose MediSeller?</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the MediSeller difference with our comprehensive pharmaceutical services
            </p>
          </div>

          {/* Advantages Grid with Images */}
          <div className="grid lg:grid-cols-2 gap-8">
            {aboutData.advantages.map((advantage, index) => {
              const Icon = iconMap[advantage.icon];
              const isEven = index % 2 === 0;

              return (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-gray-300"
                >
                  <div className={`grid ${advantage.whatsappCTA ? 'md:grid-cols-1' : 'md:grid-cols-5'}`}>
                    {/* Image */}
                    {!advantage.whatsappCTA && (
                      <div className={`md:col-span-2 ${isEven ? 'md:order-1' : 'md:order-2'}`}>
                        <div className="h-48 md:h-full relative">
                          <img
                            src={advantage.image}
                            alt={advantage.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent"></div>
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <div className={`md:col-span-3 p-8 ${isEven ? 'md:order-2' : 'md:order-1'}`}>
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-gray-900 mb-3">
                            {advantage.title}
                          </h4>
                          <p className="text-gray-600 leading-relaxed mb-4">
                            {advantage.description}
                          </p>

                          {/* WhatsApp CTA */}
                          {advantage.whatsappCTA && (
                            <Button
                              onClick={() => handleWhatsAppClick(advantage.whatsappNumber)}
                              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl flex items-center space-x-2"
                            >
                              <MessageCircle className="w-5 h-5" />
                              <span>Connect on WhatsApp</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Full width image for WhatsApp section */}
                    {advantage.whatsappCTA && (
                      <div className="h-48 relative">
                        <img
                          src={advantage.image}
                          alt={advantage.title}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent"></div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Commitment Section */}
        <div className="mt-16 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-xl overflow-hidden">
          <div className="grid md:grid-cols-2">
            <div className="p-12 space-y-6 text-white">
              <div className="inline-flex items-center space-x-2 bg-gray-700 rounded-full px-4 py-2">
                <Award className="w-5 h-5" />
                <span className="text-sm font-semibold">Our Commitment</span>
              </div>
              <h3 className="text-3xl font-bold">
                Quality You Can Trust
              </h3>
              <p className="text-gray-300 leading-relaxed">
                Every medication is sourced directly from licensed manufacturers with complete traceability. 
                We maintain the highest quality standards and ensure full compliance with international pharmaceutical regulations.
              </p>
              <div className="space-y-3 pt-4">
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  <p className="text-gray-300">Direct manufacturer partnerships</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  <p className="text-gray-300">Full batch traceability</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  <p className="text-gray-300">Regulatory compliance guaranteed</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-gray-700 to-gray-800 p-12 flex items-center justify-center">
              <div className="text-white text-center space-y-6">
                <ShieldCheck className="w-24 h-24 mx-auto" />
                <p className="text-2xl font-bold">100% Authentic Guarantee</p>
                <p className="text-gray-300">Every medication verified and certified</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
